"use client"
import { useState, useEffect } from "react"
import { useDispatch, useSelector } from "react-redux"
import { registerStudent, fetchStudents } from "../store/slices/studentSlice"
import { fetchDepartments } from "../store/slices/departmentSlice"
import { ITI_TRADES, POLY_TRADES } from "../data/trades"
import { z } from "zod" // import zod for client-side validation

import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Button,
  CircularProgress,
  Grid,
  Box,
  Typography,
  Divider,
  Paper,
  FormControlLabel,
  Checkbox,
} from "@mui/material"

const VALID_QUOTAS = ["Govt", "Private"]
const VALID_TRADE_TYPES = ["ITI", "POLYTECHNIC"]
const VALID_DURATIONS = [0.5, 1, 2, 3]

const studentSchema = z
  .object({
    council: z.enum(["SCVT", "NCVT"], { required_error: "Council is required" }),
    branch_name: z.enum(["ITI", "Polytechnic"], { required_error: "Branch is required" }),
    department_id: z.string().optional(),
    department: z.string().optional(),
    trade: z.string().min(1, "Trade is required"),
    quota: z.enum(["Govt", "Private"], { required_error: "Quota is required" }),
    name: z.string().min(2, "Name must be at least 2 characters"),
    email: z.string().email("Enter a valid email"),
    password: z.string().min(6, "Password must be at least 6 characters"),
    mobile_no: z.string().regex(/^[0-9]{10}$/, "Enter 10-digit mobile number"),
    category: z.string().optional(),
    isTechnical: z.boolean().optional(),
    durationYears: z.string().optional(),
    semester_or_year: z.string().optional(),
    dob: z
      .string()
      .min(1, "Date of Birth is required")
      .regex(/^\d{4}-\d{2}-\d{2}$/, "Invalid date format"),
    gender: z.enum(["Male", "Female", "Other"], { required_error: "Gender is required" }),
    fathers_name: z.string().min(2, "Father's name is required"),
    mothers_name: z.string().min(2, "Mother's name is required"),
    blood_group: z.enum(["A+", "A-", "B+", "B-", "AB+", "AB-", "O+", "O-"], {
      required_error: "Blood group is required",
    }),
    permanent_block: z.string().min(1, "Permanent block is required"),
    permanent_district: z.string().min(1, "Permanent district is required"),
    permanent_pin_code: z
      .string()
      .min(1, "Permanent pin code is required")
      .regex(/^[0-9]{6}$/, "Enter 6-digit pin code"),
    permanent_state: z.string().min(1, "Permanent state is required"),
    correspondence_block: z.string().optional(),
    correspondence_district: z.string().optional(),
    correspondence_pincode: z
      .string()
      .refine((val) => val === "" || /^[0-9]{6}$/.test(val), { message: "Enter 6-digit pin code if provided" }),
    correspondence_state: z.string().optional(),
    aadhar_card_no: z
      .string()
      .refine((val) => val === "" || /^[0-9]{12}$/.test(val), { message: "Aadhar must be 12 digits if provided" }),
    sub_category: z.string().optional(),
    caste_cert_no: z.string().optional(),
    driving_license_no: z.string().optional(),
    medical_fitness_cert_no: z.string().optional(),
    tc_serial_no: z.string().optional(),
    anti_ragging_reg_no: z.string().optional(),
    highschool_board_or_university: z.string().min(1, "High school board/university is required"),
    highschool_roll_no: z.string().min(1, "High school roll no is required"),
    highschool_passing_year: z
      .string()
      .min(1, "High school passing year is required")
      .regex(/^\d{4}$/, "Enter valid 4-digit year"),
    highschool_percent_marks: z
      .string()
      .min(1, "High school percent marks is required")
      .refine((val) => !isNaN(Number.parseFloat(val)) && Number.parseFloat(val) >= 0 && Number.parseFloat(val) <= 100, {
        message: "Percent must be between 0 and 100",
      }),
    highschool_max_marks: z
      .string()
      .min(1, "High school max marks is required")
      .refine((val) => !isNaN(Number.parseFloat(val)) && Number.parseFloat(val) > 0, {
        message: "Max marks must be a positive number",
      }),
    graduation_board_or_university: z.string().optional(),
    graduation_roll_no: z.string().optional(),
    graduation_passing_year: z
      .string()
      .refine((val) => val === "" || /^\d{4}$/.test(val), { message: "Enter valid 4-digit year if provided" }),
    graduation_percent_marks: z
      .string()
      .refine(
        (val) =>
          val === "" ||
          (!isNaN(Number.parseFloat(val)) && Number.parseFloat(val) >= 0 && Number.parseFloat(val) <= 100),
        {
          message: "Percent must be between 0 and 100 if provided",
        },
      ),
    graduation_max_marks: z
      .string()
      .refine((val) => val === "" || (!isNaN(Number.parseFloat(val)) && Number.parseFloat(val) > 0), {
        message: "Max marks must be a positive number if provided",
      }),
    international_board_or_university: z.string().optional(),
    international_roll_no: z.string().optional(),
    international_passing_year: z
      .string()
      .refine((val) => val === "" || /^\d{4}$/.test(val), { message: "Enter valid 4-digit year if provided" }),
    international_percent_marks: z
      .string()
      .refine(
        (val) =>
          val === "" ||
          (!isNaN(Number.parseFloat(val)) && Number.parseFloat(val) >= 0 && Number.parseFloat(val) <= 100),
        {
          message: "Percent must be between 0 and 100 if provided",
        },
      ),
    international_max_marks: z
      .string()
      .refine((val) => val === "" || (!isNaN(Number.parseFloat(val)) && Number.parseFloat(val) > 0), {
        message: "Max marks must be a positive number if provided",
      }),
    jeecup_roll_no: z.string().optional(),
    jeecup_amount: z
      .string()
      .refine((val) => val === "" || (!isNaN(Number.parseFloat(val)) && Number.parseFloat(val) >= 0), {
        message: "Amount must be a non-negative number if provided",
      }),
    jeecup_date: z
      .string()
      .refine((val) => val === "" || /^\d{4}-\d{2}-\d{2}$/.test(val), { message: "Invalid date format if provided" }),
    jeecup_transaction_no: z.string().optional(),
    scholarship: z.enum(["Yes", "No"], { required_error: "Scholarship selection is required" }),
    applied_for_hostel: z.enum(["Yes", "No"], { required_error: "Applied for hostel selection is required" }),
    admission_status: z.any().optional(),
  })
  .superRefine((data, ctx) => {
    if (data.branch_name === "ITI") {
      if (!data.durationYears || data.durationYears === "") {
        ctx.addIssue({ code: "custom", path: ["durationYears"], message: "Duration is required for ITI" })
      } else {
        const allowed = new Set(["0.5", "1", "2"])
        if (!allowed.has(String(data.durationYears))) {
          ctx.addIssue({
            code: "custom",
            path: ["durationYears"],
            message: "Duration must be 0.5, 1, or 2 years for ITI",
          })
        }
      }
      if (!data.category || data.category === "") {
        ctx.addIssue({ code: "custom", path: ["category"], message: "Category is required for ITI" })
      }
    }
  })

function calculateITIForUI({ quota, category, isTechnical, durationYears }) {
  if (!VALID_QUOTAS.includes(quota)) throw new Error("Invalid quota")
  if (!["SC_ST", "GEN_OBC", "SC", "ST", "GEN", "OBC", "General"].includes(category || ""))
    throw new Error("Invalid category")
  const normalizedCat = ["SC", "ST", "SC_ST"].includes(category) ? "SC_ST" : "GEN_OBC"
  if (typeof isTechnical !== "boolean") throw new Error("isTechnical must be a boolean")
  const dur = Number.parseFloat(durationYears)
  if (!VALID_DURATIONS.includes(dur)) throw new Error("Invalid duration")

  if (quota === "Govt") {
    const total = normalizedCat === "SC_ST" ? 300 : 780
    const notes = [
      "Govt ITI fee is fixed by category.",
      normalizedCat === "SC_ST" ? "SC/ST = ₹300" : "GEN/OBC/EWS = ₹780",
    ]
    return { total, notes }
  } else {
    const perYear = isTechnical ? 18000 : 15000
    const total = perYear * dur
    const notes = [
      `Private ITI ${isTechnical ? "Technical" : "Non-Technical"} at ₹${perYear.toLocaleString("en-IN")}/year`,
    ]
    if (dur !== 1) notes.push(`Duration × years: ${dur} → total ₹${total.toLocaleString("en-IN")}`)
    return { total, notes }
  }
}

function calculatePolyForUI(quota) {
  if (!VALID_QUOTAS.includes(quota)) throw new Error("Invalid quota")
  const total = quota === "Govt" ? 18306 : 38500
  const notes = ["Polytechnic fee is fixed by quota.", quota === "Govt" ? "Govt = ₹18,306" : "Private = ₹38,500"]
  return { total, notes }
}

function deriveBranchFromDepartment(dept) {
  const t = (dept?.type || "").toLowerCase()
  const n = (dept?.name || "").toLowerCase()
  const c = (dept?.code || "").toLowerCase()
  if (t.includes("iti") || n.includes("iti") || c.includes("iti")) return "ITI"
  if (t.includes("poly") || n.includes("polytechnic") || c.includes("poly")) return "Polytechnic"
  return "ITI"
}

const AddStudent = ({ setShowAddForm, restrictedDepartmentId = null, facultyMode = false }) => {
  const dispatch = useDispatch()
  const { loading } = useSelector((state) => state.students)
  const { departments } = useSelector((state) => state.departments)
  const { user } = useSelector((state) => state.auth)
  const [formData, setFormData] = useState({
    council: "",
    branch_name: "",
    admission_status: null,
    department_id: restrictedDepartmentId || "",
    department: "",
    scholarship: "No",
    trade: "",
    quota: "",
    isTechnical: false,
    durationYears: "",
    name: "",
    email: "",
    password: "",
    mobile_no: "",
    category: "",
    semester_or_year: "",
    dob: "",
    gender: "",
    fathers_name: "",
    mothers_name: "",
    blood_group: "",
    permanent_block: "",
    permanent_district: "",
    permanent_pin_code: "",
    permanent_state: "",
    correspondence_block: "",
    correspondence_district: "",
    correspondence_pincode: "",
    correspondence_state: "",
    aadhar_card_no: "",
    sub_category: "",
    caste_cert_no: "",
    driving_license_no: "",
    medical_fitness_cert_no: "",
    tc_serial_no: "",
    anti_ragging_reg_no: "",
    highschool_board_or_university: "",
    highschool_roll_no: "",
    highschool_passing_year: "",
    highschool_percent_marks: "",
    highschool_max_marks: "",
    graduation_board_or_university: "",
    graduation_roll_no: "",
    graduation_passing_year: "",
    graduation_percent_marks: "",
    graduation_max_marks: "",
    international_board_or_university: "",
    international_roll_no: "",
    international_passing_year: "",
    international_percent_marks: "",
    international_max_marks: "",
    jeecup_roll_no: "",
    jeecup_amount: "",
    jeecup_date: "",
    jeecup_transaction_no: "",
    applied_for_hostel: "",
  })
  const [errors, setErrors] = useState({})
  const [calculatedFee, setCalculatedFee] = useState(null)
  const [calcNotes, setCalcNotes] = useState([])
  const [profileImage, setProfileImage] = useState(null)
  const [isSameAsPermanent, setIsSameAsPermanent] = useState(false)

  useEffect(() => {
    dispatch(fetchDepartments())
  }, [dispatch])

  useEffect(() => {
    if (isSameAsPermanent) {
      setFormData((prev) => ({
        ...prev,
        correspondence_block: prev.permanent_block,
        correspondence_district: prev.permanent_district,
        correspondence_pincode: prev.permanent_pin_code,
        correspondence_state: prev.permanent_state,
      }))
    }
  }, [
    isSameAsPermanent,
    formData.permanent_block,
    formData.permanent_district,
    formData.permanent_pin_code,
    formData.permanent_state,
  ])

  useEffect(() => {
    if (!facultyMode) return
    if (!departments || departments.length === 0) return
    const deptId = restrictedDepartmentId || user?.department_id
    if (!deptId) return
    const dept = departments.find((d) => String(d.department_id) === String(deptId))
    if (!dept) return
    const inferred = deriveBranchFromDepartment(dept)
    setFormData((prev) => ({
      ...prev,
      branch_name: prev.branch_name || inferred,
      department_id: prev.department_id || String(dept.department_id),
      department: prev.department || dept.name, // Ensure department name is set
    }))
  }, [facultyMode, departments, restrictedDepartmentId, user?.department_id])

  const handleInputChange = (e) => {
    const { name, value } = e.target
    setFormData({ ...formData, [name]: value })
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: undefined }))
    }
  }

  const handleFileChange = (e) => {
    const file = e.target.files[0]
    setProfileImage(file)
  }

  const handleCalculateFeePreview = () => {
    try {
      setCalculatedFee(null)
      setCalcNotes([])
      const tradeType = (formData.branch_name || "").toUpperCase()
      if (!VALID_TRADE_TYPES.includes(tradeType)) throw new Error("Please select a valid Branch (ITI or Polytechnic).")

      if (tradeType === "ITI") {
        if (!formData.quota || !formData.category || formData.durationYears === "") {
          throw new Error("Please fill Quota, Category and Duration for ITI.")
        }
        const result = calculateITIForUI({
          quota: formData.quota,
          category: formData.category,
          isTechnical: !!formData.isTechnical,
          durationYears: formData.durationYears,
        })
        setCalculatedFee(result.total)
        setCalcNotes(result.notes)
      } else {
        if (!formData.quota) throw new Error("Please select Quota for Polytechnic.")
        const result = calculatePolyForUI(formData.quota)
        setCalculatedFee(result.total)
        setCalcNotes(result.notes)
      }
    } catch (e) {
      alert(e.message)
    }
  }

  const handleAddStudent = (e) => {
    e.preventDefault()

    const branch = formData.branch_name
    const effectiveQuota = branch === "Polytechnic" && !formData.quota ? "Govt" : formData.quota

    const dataForValidation = {
      ...formData,
      department_id: formData.department_id ? String(formData.department_id) : undefined,
      quota: effectiveQuota,
      isTechnical: !!formData.isTechnical,
      durationYears: formData.durationYears === "" ? "" : String(formData.durationYears),
    }

    const parsed = studentSchema.safeParse(dataForValidation)
    if (!parsed.success) {
      const fieldErrors = {}
      for (const issue of parsed.error.issues) {
        const key = issue.path?.[0]
        if (key) fieldErrors[key] = issue.message
      }
      setErrors(fieldErrors)
      const firstKey = Object.keys(fieldErrors)[0]
      if (firstKey) {
        const el = document.querySelector(`[name="${firstKey}"]`)
        if (el && typeof el.focus === "function") el.focus()
      }
      return
    }

    console.log("[v0] Form validation passed, submitting...")
    const formDataToSend = new FormData()
    const mappedFormData = {
      ...formData,
      quota: effectiveQuota || "",
      block: formData.permanent_block,
      district: formData.permanent_district,
      pin_code: formData.permanent_pin_code,
      state: formData.permanent_state,
      correspondence_block: formData.correspondence_block,
      correspondence_district: formData.correspondence_district,
      correspondence_pincode: formData.correspondence_pincode,
      correspondence_state: formData.correspondence_state,
    }
    delete mappedFormData.permanent_block
    delete mappedFormData.permanent_district
    delete mappedFormData.permanent_pin_code
    delete mappedFormData.permanent_state
    const cleanFormData = Object.fromEntries(
      Object.entries(mappedFormData).filter(([key, value]) => value !== "" && value !== null && value !== undefined),
    )

    if (cleanFormData.department_id !== undefined) {
      const v = cleanFormData.department_id
      cleanFormData.department_id = String(v)
    }

    if (cleanFormData.quota === undefined) cleanFormData.quota = formData.quota || ""
    cleanFormData.isTechnical = String(!!formData.isTechnical)
    cleanFormData.durationYears = formData.durationYears !== "" ? String(formData.durationYears) : ""

    // Ensure department is a string (institute name)
    if (cleanFormData.department_id) {
      const dept = departments.find((d) => String(d.department_id) === String(cleanFormData.department_id))
      cleanFormData.department = dept?.name || cleanFormData.department || ""
    }

    console.log("[v0] Sending to backend:", cleanFormData)
    Object.keys(cleanFormData).forEach((key) => {
      formDataToSend.append(key, cleanFormData[key])
    })
    if (profileImage) {
      formDataToSend.append("profile_image", profileImage)
      console.log("[v0] Profile image attached:", profileImage.name)
    }
    dispatch(registerStudent(formDataToSend))
      .unwrap()
      .then((result) => {
        console.log("[v0] Student registration completed successfully:", result)
        if (result?.auto_fee) {
          console.log("[v0] Auto fee created:", result.auto_fee)
        }
        alert("Student added successfully!")
        setShowAddForm(false)
        setFormData({
          council: "",
          branch_name: "",
          admission_status: null,
          department_id: restrictedDepartmentId || "",
          department: "",
          scholarship: "No",
          trade: "",
          quota: "",
          isTechnical: false,
          durationYears: "",
          name: "",
          email: "",
          password: "",
          mobile_no: "",
          category: "",
          semester_or_year: "",
          dob: "",
          gender: "",
          fathers_name: "",
          mothers_name: "",
          blood_group: "",
          permanent_block: "",
          permanent_district: "",
          permanent_pin_code: "",
          permanent_state: "",
          correspondence_block: "",
          correspondence_district: "",
          correspondence_pincode: "",
          correspondence_state: "",
          aadhar_card_no: "",
          sub_category: "",
          caste_cert_no: "",
          driving_license_no: "",
          medical_fitness_cert_no: "",
          tc_serial_no: "",
          anti_ragging_reg_no: "",
          highschool_board_or_university: "",
          highschool_roll_no: "",
          highschool_passing_year: "",
          highschool_percent_marks: "",
          highschool_max_marks: "",
          graduation_board_or_university: "",
          graduation_roll_no: "",
          graduation_passing_year: "",
          graduation_percent_marks: "",
          graduation_max_marks: "",
          international_board_or_university: "",
          international_roll_no: "",
          international_passing_year: "",
          international_percent_marks: "",
          international_max_marks: "",
          jeecup_roll_no: "",
          jeecup_amount: "",
          jeecup_date: "",
          jeecup_transaction_no: "",
          applied_for_hostel: "",
        })
        setCalculatedFee(null)
        setCalcNotes([])
        setProfileImage(null)
        setErrors({})
        dispatch(fetchStudents())
      })
      .catch((error) => {
        alert(`Failed to add student: ${error}`)
      })
  }

  const trades = formData.branch_name === "ITI" ? ITI_TRADES : formData.branch_name === "Polytechnic" ? POLY_TRADES : []

  const normalize = (s) => (s || "").toString().toLowerCase()
  const isDeptForBranch = (dept, branch) => {
    const br = normalize(branch)
    const dn = normalize(dept.name)
    const code = normalize(dept.code || dept.department_code)
    const db = normalize(dept.branch_name)
    if (db) return db === br || db.includes(br)
    if (br.includes("poly")) return dn.includes("polytechnic") || code.includes("poly")
    if (br.includes("iti")) return dn.includes("iti") || code.includes("iti")
    return true
  }
  const filteredDepartments = formData.branch_name
    ? departments.filter((dept) => isDeptForBranch(dept, formData.branch_name))
    : departments

  return (
    <Dialog open={true} onClose={() => setShowAddForm(false)} maxWidth="lg" fullWidth>
      <DialogTitle
        sx={{
          fontFamily: "'Times New Roman', Times, serif",
          color: "#333",
          fontSize: "1.5rem",
          p: 2,
          textAlign: "center",
        }}
      >
        {facultyMode ? "Add New Student to Institute" : "Add New Student - Bio Data Form"}
      </DialogTitle>
      <DialogContent sx={{ p: 3 }}>
        <form onSubmit={handleAddStudent} noValidate>
          <Paper elevation={3} sx={{ p: 3, borderRadius: 2 }}>
            <Box sx={{ mb: 4 }}>
              <Typography variant="h6" sx={{ fontFamily: "'Times New Roman', Times, serif", color: "#1976d2", mb: 2 }}>
                Institute & Faculty Assignment
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    select
                    label="Council"
                    name="council"
                    required
                    fullWidth
                    variant="outlined"
                    value={formData.council}
                    onChange={(e) => {
                      handleInputChange(e)
                      setFormData((prev) => ({
                        ...prev,
                        branch_name: facultyMode && prev.branch_name ? prev.branch_name : "",
                        department_id: restrictedDepartmentId || prev.department_id || "",
                        trade: "",
                        quota: "",
                        isTechnical: false,
                        durationYears: "",
                      }))
                      setCalculatedFee(null)
                      setCalcNotes([])
                      setErrors({})
                    }}
                    sx={{
                      fontFamily: "'Times New Roman', Times, serif",
                      "& .MuiSelect-select": { padding: "16px", minWidth: "150px" },
                      "& .MuiMenuItem-root": { fontFamily: "'Times New Roman', Times, serif", whiteSpace: "normal" },
                    }}
                    error={!!errors.council}
                    helperText={errors.council || ""}
                  >
                    <MenuItem value="">Select Council</MenuItem>
                    <MenuItem value="SCVT">SCVT</MenuItem>
                    <MenuItem value="NCVT">NCVT</MenuItem>
                  </TextField>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    select
                    label="Branch Name"
                    name="branch_name"
                    required
                    fullWidth
                    variant="outlined"
                    value={formData.branch_name}
                    onChange={(e) => {
                      const nextBranch = e.target.value
                      setFormData((prev) => ({
                        ...prev,
                        branch_name: nextBranch,
                        department_id: restrictedDepartmentId || prev.department_id || "",
                        department: "", // Reset department when branch changes
                        trade: "",
                        quota: "",
                        isTechnical: false,
                        durationYears: "",
                      }))
                      setCalculatedFee(null)
                      setCalcNotes([])
                      setErrors({})
                    }}
                    disabled={facultyMode || !formData.council}
                    helperText={
                      facultyMode
                        ? "Branch is locked to your institute"
                        : errors.branch_name || (!formData.council ? "Select Council first" : "")
                    }
                    sx={{
                      fontFamily: "'Times New Roman', Times, serif",
                      "& .MuiSelect-select": { padding: "16px", minWidth: "150px" },
                      "& .MuiMenuItem-root": { fontFamily: "'Times New Roman', Times, serif", whiteSpace: "normal" },
                    }}
                    error={!!errors.branch_name}
                  >
                    <MenuItem value="">Select Branch</MenuItem>
                    <MenuItem value="ITI">ITI</MenuItem>
                    <MenuItem value="Polytechnic">Polytechnic</MenuItem>
                  </TextField>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    select
                    label="Institute"
                    name="department_id"
                    fullWidth
                    variant="outlined"
                    value={formData.department_id}
                    onChange={(e) => {
                      const selectedDeptIdRaw = e.target.value
                      const selectedDeptId = String(selectedDeptIdRaw)
                      const selectedDept = departments.find(
                        (d) => String(d.department_id) === selectedDeptId
                      )
                      const derivedBranch = selectedDept ? deriveBranchFromDepartment(selectedDept) : ""
                      setFormData((prev) => ({
                        ...prev,
                        department_id: selectedDeptId,
                        department: selectedDept?.name || "",
                        branch_name:
                          (prev.branch_name && prev.branch_name !== derivedBranch && derivedBranch) ||
                          prev.branch_name ||
                          derivedBranch,
                        trade: "",
                        quota: "",
                        isTechnical: false,
                        durationYears: "",
                      }))
                      setCalculatedFee(null)
                      setCalcNotes([])
                      setErrors({})
                    }}
                    disabled={
                      !formData.council ||
                      (facultyMode && restrictedDepartmentId) ||
                      (user?.role === "FACULTY" && restrictedDepartmentId)
                    }
                    helperText={!formData.council ? "Select Council first" : errors.department_id || ""}
                    sx={{
                      fontFamily: "'Times New Roman', Times, serif",
                      "& .MuiSelect-select": { padding: "16px", minWidth: "150px" },
                      "& .MuiMenuItem-root": { fontFamily: "'Times New Roman', Times, serif", whiteSpace: "normal" },
                    }}
                    error={!!errors.department_id}
                  >
                    <MenuItem value="">Select Institute</MenuItem>
                    {filteredDepartments.map((dept) => (
                      <MenuItem key={dept.department_id} value={String(dept.department_id)}>
                        {dept.name} ({dept.code})
                      </MenuItem>
                    ))}
                  </TextField>
                  {facultyMode && restrictedDepartmentId && (
                    <Typography variant="caption" sx={{ color: "#666", mt: 1, display: "block" }}>
                      Students will be added to your institute only
                    </Typography>
                  )}
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    select
                    label="Trade"
                    name="trade"
                    required
                    fullWidth
                    variant="outlined"
                    value={formData.trade || ""}
                    onChange={(e) => {
                      const value = e.target.value
                      const selected = (
                        formData.branch_name === "ITI"
                          ? ITI_TRADES
                          : formData.branch_name === "Polytechnic"
                            ? POLY_TRADES
                            : []
                      ).find((t) => t.name === value)
                      const autoDuration =
                        formData.branch_name === "ITI" && selected
                          ? String(selected.durationYears)
                          : formData.branch_name === "Polytechnic" && selected
                            ? String(selected.durationYears)
                            : ""
                      const autoIsTechnical =
                        formData.branch_name === "ITI" && selected && typeof selected.isTechnical === "boolean"
                          ? selected.isTechnical
                          : false
                      setFormData({
                        ...formData,
                        trade: value,
                        semester_or_year: autoDuration,
                        quota: "",
                        isTechnical: autoIsTechnical,
                        durationYears: autoDuration,
                        category: "",
                      })
                      setCalculatedFee(null)
                      setCalcNotes([])
                      if (errors.trade || errors.durationYears) {
                        setErrors((prev) => ({ ...prev, trade: undefined, durationYears: undefined }))
                      }
                    }}
                    helperText={errors.trade || (!formData.branch_name ? "Select Branch first" : "")}
                    sx={{
                      fontFamily: "'Times New Roman', Times, serif",
                      "& .MuiSelect-select": { padding: "16px", minWidth: "150px" },
                      "& .MuiMenuItem-root": { fontFamily: "'Times New Roman', Times, serif", whiteSpace: "normal" },
                    }}
                    error={!!errors.trade}
                  >
                    <MenuItem value="">Select Trade</MenuItem>
                    {trades.map((trade) => (
                      <MenuItem key={trade.name} value={trade.name}>
                        {trade.name}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
              </Grid>
            </Box>

            <Box sx={{ mb: 4 }}>
              <Typography variant="h6" sx={{ fontFamily: "'Times New Roman', Times, serif", color: "#1976d2", mb: 2 }}>
                Fee Details
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6} md={4}>
                  <TextField
                    select
                    label="Quota"
                    name="quota"
                    required
                    fullWidth
                    variant="outlined"
                    value={formData.quota || ""}
                    onChange={(e) => {
                      handleInputChange(e)
                      setCalculatedFee(null)
                      setCalcNotes([])
                    }}
                    helperText={errors.quota || (!formData.branch_name ? "Select Branch first" : "")}
                    error={!!errors.quota}
                    disabled={!formData.branch_name || (facultyMode && formData.quota)}
                    sx={{
                      fontFamily: "'Times New Roman', Times, serif",
                      "& .MuiSelect-select": { padding: "16px", minWidth: "150px" },
                      "& .MuiMenuItem-root": { fontFamily: "'Times New Roman', Times, serif", whiteSpace: "normal" },
                    }}
                  >
                    <MenuItem value="">Select Quota</MenuItem>
                    <MenuItem value="Govt">Govt</MenuItem>
                    <MenuItem value="Private">Private</MenuItem>
                  </TextField>
                </Grid>

                {formData.branch_name === "ITI" && (
                  <>
                    <Grid item xs={12} sm={6} md={4}>
                      <TextField
                        select
                        label="Caste"
                        name="category"
                        required
                        fullWidth
                        variant="outlined"
                        value={formData.category || ""}
                        onChange={(e) => {
                          handleInputChange(e)
                          if (formData.branch_name === "ITI") {
                            setCalculatedFee(null)
                            setCalcNotes([])
                          }
                        }}
                        sx={{
                          fontFamily: "'Times New Roman', Times, serif",
                          "& .MuiSelect-select": { padding: "16px", minWidth: "150px" },
                          "& .MuiMenuItem-root": {
                            fontFamily: "'Times New Roman', Times, serif",
                            whiteSpace: "normal",
                          },
                        }}
                        error={!!errors.category}
                        helperText={errors.category || ""}
                      >
                        <MenuItem value="">Select Category</MenuItem>
                        <MenuItem value="SC">SC</MenuItem>
                        <MenuItem value="ST">ST</MenuItem>
                        <MenuItem value="GEN">GEN</MenuItem>
                        <MenuItem value="OBC">OBC</MenuItem>
                      </TextField>
                    </Grid>
                  </>
                )}

                <Grid item xs={12}>
                  <Box sx={{ display: "flex", gap: 2, alignItems: "center", flexWrap: "wrap" }}>
                    <Button
                      type="button"
                      onClick={handleCalculateFeePreview}
                      sx={{
                        textTransform: "none",
                        fontFamily: "'Times New Roman', Times, serif",
                        bgcolor: "#ffb300",
                        color: "#fff",
                        px: 3,
                        "&:hover": { bgcolor: "#fb8c00" },
                      }}
                    >
                      Calculate Fee
                    </Button>
                    {calculatedFee != null && (
                      <Typography
                        variant="subtitle1"
                        sx={{ fontFamily: "'Times New Roman', Times, serif", color: "#333" }}
                      >
                        Estimated Fee: ₹{calculatedFee.toLocaleString("en-IN")}
                      </Typography>
                    )}
                  </Box>
                  {calcNotes.length > 0 && (
                    <Box sx={{ mt: 1 }}>
                      {calcNotes.map((n, idx) => (
                        <Typography key={idx} variant="caption" sx={{ display: "block", color: "#666" }}>
                          • {n}
                        </Typography>
                      ))}
                    </Box>
                  )}
                </Grid>
              </Grid>
            </Box>

            <Box sx={{ mb: 4 }}>
              <Typography variant="h6" sx={{ fontFamily: "'Times New Roman', Times, serif", color: "#1976d2", mb: 2 }}>
                Basic Information
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6} md={4}>
                  <TextField
                    label="Name"
                    name="name"
                    required
                    fullWidth
                    variant="outlined"
                    value={formData.name}
                    onChange={handleInputChange}
                    sx={{ fontFamily: "'Times New Roman', Times, serif" }}
                    error={!!errors.name}
                    helperText={errors.name || ""}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <TextField
                    label="Email"
                    name="email"
                    type="email"
                    required
                    fullWidth
                    variant="outlined"
                    value={formData.email}
                    onChange={handleInputChange}
                    sx={{ fontFamily: "'Times New Roman', Times, serif" }}
                    error={!!errors.email}
                    helperText={errors.email || ""}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <TextField
                    label="Password"
                    name="password"
                    type="password"
                    required
                    fullWidth
                    variant="outlined"
                    value={formData.password}
                    onChange={handleInputChange}
                    sx={{ fontFamily: "'Times New Roman', Times, serif" }}
                    error={!!errors.password}
                    helperText={errors.password || ""}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <TextField
                    label="Mobile Number"
                    name="mobile_no"
                    type="tel"
                    pattern="[0-9]{10}"
                    maxLength="10"
                    placeholder="10-digit mobile number"
                    required
                    fullWidth
                    variant="outlined"
                    value={formData.mobile_no}
                    onChange={handleInputChange}
                    sx={{ fontFamily: "'Times New Roman', Times, serif" }}
                    error={!!errors.mobile_no}
                    helperText={errors.mobile_no || ""}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <TextField
                    label="Year"
                    name="semester_or_year"
                    fullWidth
                    variant="outlined"
                    value={formData.semester_or_year || ""}
                    onChange={handleInputChange}
                    InputLabelProps={{ shrink: true }}
                    sx={{ fontFamily: "'Times New Roman', Times, serif" }}
                    inputProps={{ type: "number", step: "0.5" }}
                    error={!!errors.semester_or_year}
                    helperText={errors.semester_or_year || ""}
                  />
                </Grid>
              </Grid>
            </Box>

            <Box sx={{ mb: 4 }}>
              <Typography variant="h6" sx={{ fontFamily: "'Times New Roman', Times, serif", color: "#1976d2", mb: 2 }}>
                Personal Information
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6} md={4}>
                  <TextField
                    label="Date of Birth"
                    name="dob"
                    type="date"
                    fullWidth
                    variant="outlined"
                    InputLabelProps={{ shrink: true }}
                    value={formData.dob}
                    onChange={handleInputChange}
                    sx={{ fontFamily: "'Times New Roman', Times, serif" }}
                    error={!!errors.dob}
                    helperText={errors.dob || ""}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <TextField
                    select
                    label="Gender"
                    name="gender"
                    fullWidth
                    variant="outlined"
                    value={formData.gender || ""}
                    onChange={handleInputChange}
                    sx={{
                      fontFamily: "'Times New Roman', Times, serif",
                      "& .MuiSelect-select": { padding: "16px", minWidth: "150px" },
                      "& .MuiMenuItem-root": { fontFamily: "'Times New Roman', Times, serif", whiteSpace: "normal" },
                    }}
                    error={!!errors.gender}
                    helperText={errors.gender || ""}
                  >
                    <MenuItem value="">Select Gender</MenuItem>
                    <MenuItem value="Male">Male</MenuItem>
                    <MenuItem value="Female">Female</MenuItem>
                    <MenuItem value="Other">Other</MenuItem>
                  </TextField>
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <TextField
                    label="Father's Name"
                    name="fathers_name"
                    fullWidth
                    variant="outlined"
                    value={formData.fathers_name}
                    onChange={handleInputChange}
                    sx={{ fontFamily: "'Times New Roman', Times, serif" }}
                    error={!!errors.fathers_name}
                    helperText={errors.fathers_name || ""}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <TextField
                    label="Mother's Name"
                    name="mothers_name"
                    fullWidth
                    variant="outlined"
                    value={formData.mothers_name}
                    onChange={handleInputChange}
                    sx={{ fontFamily: "'Times New Roman', Times, serif" }}
                    error={!!errors.mothers_name}
                    helperText={errors.mothers_name || ""}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <TextField
                    select
                    label="Blood Group"
                    name="blood_group"
                    fullWidth
                    variant="outlined"
                    value={formData.blood_group || ""}
                    onChange={handleInputChange}
                    sx={{
                      fontFamily: "'Times New Roman', Times, serif",
                      "& .MuiSelect-select": { padding: "16px", minWidth: "150px" },
                      "& .MuiMenuItem-root": { fontFamily: "'Times New Roman', Times, serif", whiteSpace: "normal" },
                    }}
                    error={!!errors.blood_group}
                    helperText={errors.blood_group || ""}
                  >
                    <MenuItem value="">Select Blood Group</MenuItem>
                    <MenuItem value="A+">A+</MenuItem>
                    <MenuItem value="A-">A-</MenuItem>
                    <MenuItem value="B+">B+</MenuItem>
                    <MenuItem value="B-">B-</MenuItem>
                    <MenuItem value="AB+">AB+</MenuItem>
                    <MenuItem value="AB-">AB-</MenuItem>
                    <MenuItem value="O+">O+</MenuItem>
                    <MenuItem value="O-">O-</MenuItem>
                  </TextField>
                </Grid>
              </Grid>
            </Box>

            <Box sx={{ mb: 4 }}>
              <Typography variant="h6" sx={{ fontFamily: "'Times New Roman', Times, serif", color: "#1976d2", mb: 2 }}>
                Address Information
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Typography
                    variant="subtitle1"
                    sx={{ fontFamily: "'Times New Roman', Times, serif", color: "#555", mb: 1 }}
                  >
                    Permanent Address
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    label="Block"
                    name="permanent_block"
                    fullWidth
                    variant="outlined"
                    value={formData.permanent_block}
                    onChange={handleInputChange}
                    sx={{ fontFamily: "'Times New Roman', Times, serif" }}
                    error={!!errors.permanent_block}
                    helperText={errors.permanent_block || ""}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    label="District"
                    name="permanent_district"
                    fullWidth
                    variant="outlined"
                    value={formData.permanent_district}
                    onChange={handleInputChange}
                    sx={{ fontFamily: "'Times New Roman', Times, serif" }}
                    error={!!errors.permanent_district}
                    helperText={errors.permanent_district || ""}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    label="Pin Code"
                    name="permanent_pin_code"
                    fullWidth
                    variant="outlined"
                    value={formData.permanent_pin_code}
                    onChange={handleInputChange}
                    sx={{ fontFamily: "'Times New Roman', Times, serif" }}
                    error={!!errors.permanent_pin_code}
                    helperText={errors.permanent_pin_code || ""}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    label="State"
                    name="permanent_state"
                    fullWidth
                    variant="outlined"
                    value={formData.permanent_state}
                    onChange={handleInputChange}
                    sx={{ fontFamily: "'Times New Roman', Times, serif" }}
                    error={!!errors.permanent_state}
                    helperText={errors.permanent_state || ""}
                  />
                </Grid>
                <Grid item xs={12}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={isSameAsPermanent}
                        onChange={(e) => setIsSameAsPermanent(e.target.checked)}
                        color="primary"
                      />
                    }
                    label="Correspondence address is the same as permanent address"
                    sx={{ fontFamily: "'Times New Roman', Times, serif", width: "1000px", color: "#555", mb: 1, mt: 2 }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Typography
                    variant="subtitle1"
                    sx={{ fontFamily: "'Times New Roman', Times, serif", color: "#555", mb: 1, mt: 2 }}
                  >
                    Corresp. Address
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    label="Block"
                    name="correspondence_block"
                    fullWidth
                    variant="outlined"
                    value={isSameAsPermanent ? formData.permanent_block : formData.correspondence_block}
                    onChange={isSameAsPermanent ? () => {} : handleInputChange}
                    disabled={isSameAsPermanent}
                    sx={{ fontFamily: "'Times New Roman', Times, serif" }}
                    error={!!errors.correspondence_block}
                    helperText={errors.correspondence_block || ""}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    label="District"
                    name="correspondence_district"
                    fullWidth
                    variant="outlined"
                    value={isSameAsPermanent ? formData.permanent_district : formData.correspondence_district}
                    onChange={isSameAsPermanent ? () => {} : handleInputChange}
                    disabled={isSameAsPermanent}
                    sx={{ fontFamily: "'Times New Roman', Times, serif" }}
                    error={!!errors.correspondence_district}
                    helperText={errors.correspondence_district || ""}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    label="Pin Code"
                    name="correspondence_pincode"
                    fullWidth
                    variant="outlined"
                    value={isSameAsPermanent ? formData.permanent_pin_code : formData.correspondence_pincode}
                    onChange={isSameAsPermanent ? () => {} : handleInputChange}
                    disabled={isSameAsPermanent}
                    sx={{ fontFamily: "'Times New Roman', Times, serif" }}
                    error={!!errors.correspondence_pincode}
                    helperText={errors.correspondence_pincode || ""}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    label="State"
                    name="correspondence_state"
                    fullWidth
                    variant="outlined"
                    value={isSameAsPermanent ? formData.permanent_state : formData.correspondence_state}
                    onChange={isSameAsPermanent ? () => {} : handleInputChange}
                    disabled={isSameAsPermanent}
                    sx={{ fontFamily: "'Times New Roman', Times, serif" }}
                    error={!!errors.correspondence_state}
                    helperText={errors.correspondence_state || ""}
                  />
                </Grid>
              </Grid>
            </Box>

            <Box sx={{ mb: 4 }}>
              <Typography variant="h6" sx={{ fontFamily: "'Times New Roman', Times, serif", color: "#1976d2", mb: 2 }}>
                Document Information (Aadhar, Caste, etc.)
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6} md={4}>
                  <TextField
                    label="Aadhar Card Number"
                    name="aadhar_card_no"
                    pattern="[0-9]{12}"
                    maxLength="12"
                    placeholder="12-digit Aadhar number"
                    fullWidth
                    variant="outlined"
                    value={formData.aadhar_card_no}
                    onChange={handleInputChange}
                    sx={{ fontFamily: "'Times New Roman', Times, serif" }}
                    error={!!errors.aadhar_card_no}
                    helperText={errors.aadhar_card_no || ""}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <TextField
                    select
                    label="Caste"
                    name="category"
                    fullWidth
                    variant="outlined"
                    value={formData.category || ""}
                    onChange={(e) => {
                      handleInputChange(e)
                      if (formData.branch_name === "ITI") {
                        setCalculatedFee(null)
                        setCalcNotes([])
                      }
                    }}
                    sx={{
                      fontFamily: "'Times New Roman', Times, serif",
                      "& .MuiSelect-select": { padding: "16px", minWidth: "150px" },
                      "& .MuiMenuItem-root": { fontFamily: "'Times New Roman', Times, serif", whiteSpace: "normal" },
                    }}
                    error={!!errors.category}
                    helperText={errors.category || ""}
                  >
                    <MenuItem value="">Select Category</MenuItem>
                    <MenuItem value="General">General</MenuItem>
                    <MenuItem value="OBC">OBC</MenuItem>
                    <MenuItem value="SC">SC</MenuItem>
                    <MenuItem value="ST">ST</MenuItem>
                  </TextField>
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <TextField
                    label="Sub Category"
                    name="sub_category"
                    fullWidth
                    variant="outlined"
                    value={formData.sub_category}
                    onChange={handleInputChange}
                    sx={{ fontFamily: "'Times New Roman', Times, serif" }}
                    error={!!errors.sub_category}
                    helperText={errors.sub_category || ""}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <TextField
                    label="Caste Certificate No"
                    name="caste_cert_no"
                    fullWidth
                    variant="outlined"
                    value={formData.caste_cert_no}
                    onChange={handleInputChange}
                    sx={{ fontFamily: "'Times New Roman', Times, serif" }}
                    error={!!errors.caste_cert_no}
                    helperText={errors.caste_cert_no || ""}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <TextField
                    label="Driving License No"
                    name="driving_license_no"
                    fullWidth
                    variant="outlined"
                    value={formData.driving_license_no}
                    onChange={handleInputChange}
                    sx={{ fontFamily: "'Times New Roman', Times, serif" }}
                    error={!!errors.driving_license_no}
                    helperText={errors.driving_license_no || ""}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <TextField
                    label="Medical Fitness Certificate No"
                    name="medical_fitness_cert_no"
                    fullWidth
                    variant="outlined"
                    value={formData.medical_fitness_cert_no}
                    onChange={handleInputChange}
                    sx={{ fontFamily: "'Times New Roman', Times, serif" }}
                    error={!!errors.medical_fitness_cert_no}
                    helperText={errors.medical_fitness_cert_no || ""}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <TextField
                    label="TC Serial No"
                    name="tc_serial_no"
                    fullWidth
                    variant="outlined"
                    value={formData.tc_serial_no}
                    onChange={handleInputChange}
                    sx={{ fontFamily: "'Times New Roman', Times, serif" }}
                    error={!!errors.tc_serial_no}
                    helperText={errors.tc_serial_no || ""}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <TextField
                    label="Anti Ragging Registration No"
                    name="anti_ragging_reg_no"
                    fullWidth
                    variant="outlined"
                    value={formData.anti_ragging_reg_no}
                    onChange={handleInputChange}
                    sx={{ fontFamily: "'Times New Roman', Times, serif" }}
                    error={!!errors.anti_ragging_reg_no}
                    helperText={errors.anti_ragging_reg_no || ""}
                  />
                </Grid>
              </Grid>
            </Box>

            <Box sx={{ mb: 4 }}>
              <Typography variant="h6" sx={{ fontFamily: "'Times New Roman', Times, serif", color: "#1976d2", mb: 2 }}>
                Academic Information
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Typography
                    variant="subtitle1"
                    sx={{ fontFamily: "'Times New Roman', Times, serif", color: "#555", mb: 1 }}
                  >
                    High School
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <TextField
                    label="Board/University"
                    name="highschool_board_or_university"
                    fullWidth
                    variant="outlined"
                    value={formData.highschool_board_or_university}
                    onChange={handleInputChange}
                    sx={{ fontFamily: "'Times New Roman', Times, serif" }}
                    error={!!errors.highschool_board_or_university}
                    helperText={errors.highschool_board_or_university || ""}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <TextField
                    label="Roll No"
                    name="highschool_roll_no"
                    fullWidth
                    variant="outlined"
                    value={formData.highschool_roll_no}
                    onChange={handleInputChange}
                    sx={{ fontFamily: "'Times New Roman', Times, serif" }}
                    error={!!errors.highschool_roll_no}
                    helperText={errors.highschool_roll_no || ""}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <TextField
                    label="Passing Year"
                    name="highschool_passing_year"
                    type="number"
                    fullWidth
                    variant="outlined"
                    value={formData.highschool_passing_year}
                    onChange={handleInputChange}
                    sx={{ fontFamily: "'Times New Roman', Times, serif" }}
                    error={!!errors.highschool_passing_year}
                    helperText={errors.highschool_passing_year || ""}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <TextField
                    label="Percent Marks"
                    name="highschool_percent_marks"
                    type="number"
                    step="0.01"
                    fullWidth
                    variant="outlined"
                    value={formData.highschool_percent_marks}
                    onChange={handleInputChange}
                    sx={{ fontFamily: "'Times New Roman', Times, serif" }}
                    error={!!errors.highschool_percent_marks}
                    helperText={errors.highschool_percent_marks || ""}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <TextField
                    label="Max Marks"
                    name="highschool_max_marks"
                    type="number"
                    fullWidth
                    variant="outlined"
                    value={formData.highschool_max_marks}
                    onChange={handleInputChange}
                    sx={{ fontFamily: "'Times New Roman', Times, serif" }}
                    error={!!errors.highschool_max_marks}
                    helperText={errors.highschool_max_marks || ""}
                  />
                </Grid>

                <Grid item xs={12}>
                  <Typography
                    variant="subtitle1"
                    sx={{ fontFamily: "'Times New Roman', Times, serif", color: "#555", mb: 1, mt: 2 }}
                  >
                    Graduation
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <TextField
                    label="Board/University"
                    name="graduation_board_or_university"
                    fullWidth
                    variant="outlined"
                    value={formData.graduation_board_or_university}
                    onChange={handleInputChange}
                    sx={{ fontFamily: "'Times New Roman', Times, serif" }}
                    error={!!errors.graduation_board_or_university}
                    helperText={errors.graduation_board_or_university || ""}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <TextField
                    label="Roll No"
                    name="graduation_roll_no"
                    fullWidth
                    variant="outlined"
                    value={formData.graduation_roll_no}
                    onChange={handleInputChange}
                    sx={{ fontFamily: "'Times New Roman', Times, serif" }}
                    error={!!errors.graduation_roll_no}
                    helperText={errors.graduation_roll_no || ""}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <TextField
                    label="Passing Year"
                    name="graduation_passing_year"
                    type="number"
                    fullWidth
                    variant="outlined"
                    value={formData.graduation_passing_year}
                    onChange={handleInputChange}
                    sx={{ fontFamily: "'Times New Roman', Times, serif" }}
                    error={!!errors.graduation_passing_year}
                    helperText={errors.graduation_passing_year || ""}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <TextField
                    label="Percent Marks"
                    name="graduation_percent_marks"
                    type="number"
                    step="0.01"
                    fullWidth
                    variant="outlined"
                    value={formData.graduation_percent_marks}
                    onChange={handleInputChange}
                    sx={{ fontFamily: "'Times New Roman', Times, serif" }}
                    error={!!errors.graduation_percent_marks}
                    helperText={errors.graduation_percent_marks || ""}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <TextField
                    label="Max Marks"
                    name="graduation_max_marks"
                    type="number"
                    fullWidth
                    variant="outlined"
                    value={formData.graduation_max_marks}
                    onChange={handleInputChange}
                    sx={{ fontFamily: "'Times New Roman', Times, serif" }}
                    error={!!errors.graduation_max_marks}
                    helperText={errors.graduation_max_marks || ""}
                  />
                </Grid>

                <Grid item xs={12}>
                  <Typography
                    variant="subtitle1"
                    sx={{ fontFamily: "'Times New Roman', Times, serif", color: "#555", mb: 1, mt: 2 }}
                  >
                    International
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <TextField
                    label="Board/University"
                    name="international_board_or_university"
                    fullWidth
                    variant="outlined"
                    value={formData.international_board_or_university}
                    onChange={handleInputChange}
                    sx={{ fontFamily: "'Times New Roman', Times, serif" }}
                    error={!!errors.international_board_or_university}
                    helperText={errors.international_board_or_university || ""}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <TextField
                    label="Roll No"
                    name="international_roll_no"
                    fullWidth
                    variant="outlined"
                    value={formData.international_roll_no}
                    onChange={handleInputChange}
                    sx={{ fontFamily: "'Times New Roman', Times, serif" }}
                    error={!!errors.international_roll_no}
                    helperText={errors.international_roll_no || ""}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <TextField
                    label="Passing Year"
                    name="international_passing_year"
                    type="number"
                    fullWidth
                    variant="outlined"
                    InputLabelProps={{ shrink: true }}
                    value={formData.international_passing_year}
                    onChange={handleInputChange}
                    sx={{ fontFamily: "'Times New Roman', Times, serif" }}
                    error={!!errors.international_passing_year}
                    helperText={errors.international_passing_year || ""}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <TextField
                    label="Percent Marks"
                    name="international_percent_marks"
                    type="number"
                    step="0.01"
                    fullWidth
                    variant="outlined"
                    value={formData.international_percent_marks}
                    onChange={handleInputChange}
                    sx={{ fontFamily: "'Times New Roman', Times, serif" }}
                    error={!!errors.international_percent_marks}
                    helperText={errors.international_percent_marks || ""}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <TextField
                    label="Max Marks"
                    name="international_max_marks"
                    type="number"
                    fullWidth
                    variant="outlined"
                    value={formData.international_max_marks}
                    onChange={handleInputChange}
                    sx={{ fontFamily: "'Times New Roman', Times, serif" }}
                    error={!!errors.international_max_marks}
                    helperText={errors.international_max_marks || ""}
                  />
                </Grid>
              </Grid>
            </Box>

            <Box sx={{ mb: 4 }}>
              <Typography variant="h6" sx={{ fontFamily: "'Times New Roman', Times, serif", color: "#1976d2", mb: 2 }}>
                JEECUP Information
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    label="Roll No"
                    name="jeecup_roll_no"
                    fullWidth
                    variant="outlined"
                    value={formData.jeecup_roll_no}
                    onChange={handleInputChange}
                    sx={{ fontFamily: "'Times New Roman', Times, serif" }}
                    error={!!errors.jeecup_roll_no}
                    helperText={errors.jeecup_roll_no || ""}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    label="Amount"
                    name="jeecup_amount"
                    type="number"
                    step="0.01"
                    fullWidth
                    variant="outlined"
                    value={formData.jeecup_amount}
                    onChange={handleInputChange}
                    sx={{ fontFamily: "'Times New Roman', Times, serif" }}
                    error={!!errors.jeecup_amount}
                    helperText={errors.jeecup_amount || ""}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    label="Date"
                    name="jeecup_date"
                    type="date"
                    fullWidth
                    variant="outlined"
                    InputLabelProps={{ shrink: true }}
                    value={formData.jeecup_date}
                    onChange={handleInputChange}
                    sx={{ fontFamily: "'Times New Roman', Times, serif" }}
                    error={!!errors.jeecup_date}
                    helperText={errors.jeecup_date || ""}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    label="Transaction No"
                    name="jeecup_transaction_no"
                    fullWidth
                    variant="outlined"
                    value={formData.jeecup_transaction_no}
                    onChange={handleInputChange}
                    sx={{ fontFamily: "'Times New Roman', Times, serif" }}
                    error={!!errors.jeecup_transaction_no}
                    helperText={errors.jeecup_transaction_no || ""}
                  />
                </Grid>
              </Grid>
            </Box>

            <Box sx={{ mb: 4 }}>
              <Typography variant="h6" sx={{ fontFamily: "'Times New Roman', Times, serif", color: "#1976d2", mb: 2 }}>
                Other Information
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6} md={4}>
                  <TextField
                    select
                    label="Scholarship"
                    name="scholarship"
                    fullWidth
                    variant="outlined"
                    value={formData.scholarship || "No"}
                    onChange={handleInputChange}
                    sx={{
                      fontFamily: "'Times New Roman', Times, serif",
                      "& .MuiSelect-select": { padding: "16px", minWidth: "150px" },
                      "& .MuiMenuItem-root": { fontFamily: "'Times New Roman', Times, serif", whiteSpace: "normal" },
                    }}
                    error={!!errors.scholarship}
                    helperText={errors.scholarship || ""}
                  >
                    <MenuItem value="No">No</MenuItem>
                    <MenuItem value="Yes">Yes</MenuItem>
                  </TextField>
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <TextField
                    select
                    label="Applied for Hostel"
                    name="applied_for_hostel"
                    fullWidth
                    variant="outlined"
                    value={formData.applied_for_hostel || ""}
                    onChange={handleInputChange}
                    sx={{
                      fontFamily: "'Times New Roman', Times, serif",
                      "& .MuiSelect-select": { padding: "16px", minWidth: "150px" },
                      "& .MuiMenuItem-root": { fontFamily: "'Times New Roman', Times, serif", whiteSpace: "normal" },
                    }}
                    error={!!errors.applied_for_hostel}
                    helperText={errors.applied_for_hostel || ""}
                  >
                    <MenuItem value="">Select</MenuItem>
                    <MenuItem value="Yes">Yes</MenuItem>
                    <MenuItem value="No">No</MenuItem>
                  </TextField>
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <Box sx={{ mb: 2 }}>
                    <Typography
                      variant="body1"
                      sx={{ fontFamily: "'Times New Roman', Times, serif", color: "#333", mb: 1 }}
                    >
                      Profile Photo
                    </Typography>
                    <Button
                      variant="outlined"
                      component="label"
                      sx={{
                        fontFamily: "'Times New Roman', Times, serif",
                        borderColor: "#1976d2",
                        color: "#1976d2",
                        "&:hover": { borderColor: "#115293", color: "#115293" },
                      }}
                    >
                      Upload Photo
                      <input type="file" name="profile_image" accept="image/*" hidden onChange={handleFileChange} />
                    </Button>
                    {profileImage && (
                      <Box sx={{ mt: 2, textAlign: "center" }}>
                        <Typography
                          variant="caption"
                          sx={{ fontFamily: "'Times New Roman', Times, serif", color: "#555", display: "block", mb: 1 }}
                        >
                          Selected: {profileImage.name}
                        </Typography>
                        <img
                          src={URL.createObjectURL(profileImage) || "/placeholder.svg"}
                          alt="Profile Preview"
                          style={{
                            width: 120,
                            height: 120,
                            objectFit: "cover",
                            border: "2px solid #1976d2",
                            borderRadius: "50%",
                            boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                          }}
                        />
                      </Box>
                    )}
                  </Box>
                </Grid>
              </Grid>
            </Box>
          </Paper>
        </form>
      </DialogContent>
      <DialogActions sx={{ p: 2, justifyContent: "center"}}>
        <Button
          onClick={() => {
            setShowAddForm(false)
            setFormData({
              council: "",
              branch_name: "",
              admission_status: null,
              department_id: restrictedDepartmentId || "",
              department: "",
              scholarship: "No",
              trade: "",
              quota: "",
              isTechnical: false,
              durationYears: "",
              name: "",
              email: "",
              password: "",
              mobile_no: "",
              category: "",
              semester_or_year: "",
              dob: "",
              gender: "",
              fathers_name: "",
              mothers_name: "",
              blood_group: "",
              permanent_block: "",
              permanent_district: "",
              permanent_pin_code: "",
              permanent_state: "",
              correspondence_block: "",
              correspondence_district: "",
              correspondence_pincode: "",
              correspondence_state: "",
              aadhar_card_no: "",
              sub_category: "",
              caste_cert_no: "",
              driving_license_no: "",
              medical_fitness_cert_no: "",
              tc_serial_no: "",
              anti_ragging_reg_no: "",
              highschool_board_or_university: "",
              highschool_roll_no: "",
              highschool_passing_year: "",
              highschool_percent_marks: "",
              highschool_max_marks: "",
              graduation_board_or_university: "",
              graduation_roll_no: "",
              graduation_passing_year: "",
              graduation_percent_marks: "",
              graduation_max_marks: "",
              international_board_or_university: "",
              international_roll_no: "",
              international_passing_year: "",
              international_percent_marks: "",
              international_max_marks: "",
              jeecup_roll_no: "",
              jeecup_amount: "",
              jeecup_date: "",
              jeecup_transaction_no: "",
              applied_for_hostel: "",
            })
            setIsSameAsPermanent(true)
            setCalculatedFee(null)
            setCalcNotes([])
            setProfileImage(null)
            setErrors({})
          }}
          sx={{
            textTransform: "none",
            fontFamily: "'Times New Roman', Times, serif",
            color: "#f44336",
            border: "1px solid #f44336",
            px: 3,
            py: 1,
            mr: 2,
            "&:hover": { bgcolor: "#f44336", color: "#fff" },
          }}
        >
          Cancel
        </Button>
        <Button
          type="submit"
          onClick={handleAddStudent}
          disabled={loading}
          sx={{
            textTransform: "none",
            fontFamily: "'Times New Roman', Times, serif",
            bgcolor: "#1976d2",
            color: "#fff",
            px: 3,
            py: 1,
            "&:hover": { bgcolor: "#115293" },
            "&:disabled": { bgcolor: "#ccc", color: "#fff" },
          }}
        >
          {loading ? <CircularProgress size={24} sx={{ color: "#fff" }} /> : "Add Student"}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default AddStudent